/**
 * The meeting editor (TMMAPP-0103, audit A4 §3, P:2297-2452): 700 wide, dense fields, the notes at
 * the top (read-only / past), a schedule box with start and end (the end fields derive from and edit
 * `durationMinutes`; the end zone is the start zone, shown read-only: MM-D-26) and the product's one
 * recurrence rule inline, an invitations list with a toggle pill per sharer, the notes and the
 * reminder. It opens for every viewer: a viewer who can't write sees it read-only. The cancel-meeting
 * confirmation replaces the form in this same dialog (DLG-005).
 *
 * Standalone meetings (TMMAPP-0159 §D 20–21, MM-D-150): from My Money Meetings a new meeting first
 * chooses **About a virtual account** (today's rules and routes) or **Just us** (no virtual account,
 * talk only). A Just us meeting invites from the caller's sharing pool (`GET /v1/me/meeting-people`),
 * at least one person; a person's id is only ever sent back, never shown. Its definition and
 * invitations are the organizer's alone: anyone else reads it.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { backendNumber } from '@/api/errorDetails';
import { ErrorCodes, isApiError } from '@/api/errors';
import type {
  Meeting,
  MeetingCreateRequest,
  MeetingHome,
  MeetingPatchRequest,
  MyMeetingCreateRequest,
  PoolPerson,
} from '@/api/meetings';
import { meetingsApi, recurrenceToWire } from '@/api/meetings';
import { sharesApi, type Share } from '@/api/shares';
import type { RecurrenceRule } from '@/api/planned';
import { ConfirmPanel } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { denseFieldSx } from '@/components/denseField';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { GuardedDialog } from '@/components/GuardedDialog';
import { Pill } from '@/components/Pill';
import { SegmentedControl } from '@/components/SegmentedControl';
import { SubmitButton } from '@/components/SubmitButton';
import { useReturnFocus } from '@/components/useConfirm';
import { RecurrenceInline } from '@/features/planned/recurrence/RecurrenceInline';
import { emptyRule, validateRule } from '@/features/planned/recurrence/recurrence';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { defaultTimezone, isValidTimezone } from '@/features/subscribe/timezone';
import { personName } from '@/i18n/personName';
import { sameUtcInstant, utcToZonedCivil, zonedCivilToUtc } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { initialsOf } from '@/shell/initials';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { resolveTokens } from '@/theme/theme';

const TITLE_ID = 'meeting-editor-title';
const SUBTITLE_ID = 'meeting-editor-subtitle';
const CANCEL_TITLE_ID = 'meeting-cancel-title';
const CANCEL_OPENER = 'meeting-cancel';
const MEETING_FIELDS = [
  'title',
  'startsAt',
  'durationMinutes',
  'seriesTimezone',
  'inviteUserIds',
  'meetingUrl',
  'reminderLeadMinutes',
  'locationText',
  'description',
] as const;
type MeetingField = (typeof MEETING_FIELDS)[number];
const REMINDERS: { value: number | ''; key: string }[] = [
  { value: '', key: 'reminderNone' },
  { value: 15, key: 'reminder15' },
  { value: 60, key: 'reminder60' },
  { value: 1440, key: 'reminder1440' },
  { value: 10080, key: 'reminder10080' },
];

function zoneList(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [];
  }
}

const MS_PER_MINUTE = 60_000;

/** The civil date and time `minutes` after a start, in `zone`; null when the start is incomplete. */
function addMinutes(
  date: string,
  time: string,
  zone: string,
  minutes: number,
): { date: string; time: string } | null {
  if (date.length === 0 || time.length === 0 || !isValidTimezone(zone)) return null;
  try {
    const start = Date.parse(zonedCivilToUtc(date, time, zone));
    return utcToZonedCivil(new Date(start + minutes * MS_PER_MINUTE).toISOString(), zone);
  } catch {
    return null;
  }
}

/** Whole minutes from the start to the end in `zone`; null when either is incomplete. */
function minutesBetween(
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
  zone: string,
): number | null {
  if (startDate.length === 0 || startTime.length === 0) return null;
  if (endDate.length === 0 || endTime.length === 0 || !isValidTimezone(zone)) return null;
  try {
    const from = Date.parse(zonedCivilToUtc(startDate, startTime, zone));
    const to = Date.parse(zonedCivilToUtc(endDate, endTime, zone));
    return Math.round((to - from) / MS_PER_MINUTE);
  } catch {
    return null;
  }
}

export interface VirtualAccountChoice {
  id: number;
  name: string;
}

type About = 'virtualAccount' | 'justUs';

interface MeetingEditorDialogProps {
  /**
   * The meeting's home: the virtual account it's on, or `null` for a standalone meeting being edited;
   * omitted when `choices` lets a new meeting pick one.
   */
  virtualAccountId?: MeetingHome;
  /** A new meeting's virtual account picker (the viewer's owned, active virtual accounts). */
  choices?: readonly VirtualAccountChoice[];
  /** A new meeting may be Just us (My Money Meetings' New meeting, TMMAPP-0159 §D 20). */
  allowStandalone?: boolean;
  meeting?: Meeting;
  /** The viewer can't change this meeting: the fields are disabled and nothing saves. */
  readOnly?: boolean;
  /** The occurrence has happened: the schedule is locked and only the notes can change. */
  past?: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}

export function MeetingEditorDialog({
  virtualAccountId,
  choices = [],
  allowStandalone = false,
  meeting,
  readOnly = false,
  past = false,
  onClose,
  onSaved,
}: MeetingEditorDialogProps) {
  const { t } = useTranslation();
  const me = useMe();
  const openSubscribe = useOpenSubscribe();
  const tokens = resolveTokens(useTheme());
  const dense = denseFieldSx(tokens);
  const [pickedVirtualAccountId, setPickedVirtualAccountId] = useState<number | null>(
    virtualAccountId ?? choices[0]?.id ?? null,
  );
  // A meeting being edited keeps its kind; a new one from My Money Meetings chooses it, Just us
  // first when the viewer has no virtual account to put it on.
  const [about, setAbout] = useState<About>(() =>
    meeting !== undefined
      ? meeting.kind === 'STANDALONE'
        ? 'justUs'
        : 'virtualAccount'
      : virtualAccountId === null || (allowStandalone && choices.length === 0)
        ? 'justUs'
        : 'virtualAccount',
  );
  const standalone = about === 'justUs';
  const vaId = standalone ? null : (virtualAccountId ?? pickedVirtualAccountId);
  const virtualAccount = me.virtualAccounts.find((row) => row.id === vaId);
  const virtualAccountName = virtualAccount?.name ?? choices.find((c) => c.id === vaId)?.name ?? '';
  const zones = useMemo(() => zoneList(), []);
  const defaultZone =
    me.user.timezone && isValidTimezone(me.user.timezone) ? me.user.timezone : defaultTimezone();
  const initialZone = meeting?.seriesTimezone ?? defaultZone;
  const initial = meeting
    ? utcToZonedCivil(meeting.startsAt, initialZone)
    : { date: '', time: '09:00' };
  const initialEnd = meeting
    ? (addMinutes(initial.date, initial.time, initialZone, meeting.durationMinutes) ?? {
        date: '',
        time: '',
      })
    : { date: '', time: '10:00' };
  const [title, setTitle] = useState(meeting?.title ?? '');
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [zone, setZone] = useState(initialZone);
  const [endDate, setEndDate] = useState(initialEnd.date);
  const [endTime, setEndTime] = useState(initialEnd.time);
  const [url, setUrl] = useState(meeting?.meetingUrl ?? '');
  const [location, setLocation] = useState(meeting?.locationText ?? '');
  const [description, setDescription] = useState(meeting?.description ?? '');
  const [reminder, setReminder] = useState<number | ''>(meeting?.reminderLeadMinutes ?? '');
  const [rule, setRule] = useState<RecurrenceRule>(
    meeting?.recurrence ?? emptyRule(initial.date || '2026-01-01'),
  );
  // The invite set as the meeting serves it; an edit sends the set only when the person changed it
  // (TMMAPP-0160 item 12), so an invitee the page can't list is never silently uninvited.
  const [servedInvites] = useState<number[]>(() =>
    (meeting?.invitees ?? [])
      .flatMap((i) => (i.userId === null || i.status === 'CANCELLED' ? [] : [i.userId]))
      .sort((a, b) => a - b),
  );
  const [inviteIds, setInviteIds] = useState<Set<number>>(() => new Set(servedInvites));
  const [shares, setShares] = useState<Share[] | null>(null);
  // A Just us meeting's people: the caller's sharing pool, re-read after a refusal.
  const [pool, setPool] = useState<PoolPerson[] | null>(null);
  const [poolRead, setPoolRead] = useState(0);
  // A failed pool read: its own error and Retry, never "you share with no one" (that's a served []).
  const [poolFailure, setPoolFailure] = useState<{ error: unknown } | null>(null);
  // `403 MEETING_SCHEDULE_NOT_ALLOWED`: the purchase line and its way through (§D 21).
  const [needsPlan, setNeedsPlan] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<MeetingField, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const locked = readOnly;
  const scheduleLocked = locked || past;

  const minutes = minutesBetween(date, time, endDate, endTime, zone);
  const { dirty } = useDirty({
    about,
    vaId,
    title,
    date,
    time,
    zone,
    endDate,
    endTime,
    url,
    location,
    description,
    reminder,
    rule,
    invitees: [...inviteIds].sort((a, b) => a - b),
  });
  const guard = useDialogDirtyGuard(dirty && !locked, onClose, submitting);
  useReturnFocus(cancelling, CANCEL_OPENER);

  // The people who can be invited are the virtual account's sharers. Only its owner can list them;
  // a viewer who can't write reads the meeting's own invitees instead.
  useEffect(() => {
    if (vaId === null || locked) return;
    let cancelled = false;
    void sharesApi
      .list(vaId)
      .then((rows) => {
        if (!cancelled) setShares(rows);
      })
      .catch(() => {
        if (!cancelled) setShares([]);
      });
    return () => {
      cancelled = true;
    };
  }, [vaId, locked]);

  // A Just us meeting invites from the caller's pool: anyone they share a virtual account with.
  useEffect(() => {
    if (!standalone || locked) return;
    let cancelled = false;
    void meetingsApi
      .listMeetingPeople()
      .then((rows) => {
        if (cancelled) return;
        setPool(rows);
        setPoolFailure(null);
      })
      .catch((error: unknown) => {
        if (!cancelled) setPoolFailure({ error });
      });
    return () => {
      cancelled = true;
    };
  }, [standalone, locked, poolRead]);

  const active = (shares ?? []).filter((s) => s.status === 'ACTIVE' && s.userId !== null);
  const pending = (shares ?? []).filter((s) => s.status === 'INVITED');
  const sharedWith = active.map((s) => personName(s.displayName, t));
  const civilRule = { ...rule, startOn: date || rule.startOn };
  const ruleError = civilRule.frequency === 'ONCE' ? null : validateRule(civilRule);
  const zoneOptions = zones.includes(zone) ? zones : [zone, ...zones];

  function errorMessage(err: unknown): string {
    if (!isApiError(err)) return t('meetings.errorGeneric');
    if (err.code === ErrorCodes.INVITEE_NOT_SHARER) return t('meetings.errorInvitee');
    if (err.code === ErrorCodes.INVITEE_NOT_IN_POOL) {
      // `details.userId`: the person no longer in the pool.
      const id = backendNumber(err, 'userId');
      const named =
        id === null
          ? undefined
          : [...(pool ?? []), ...(meeting?.invitees ?? [])].find((person) => person.userId === id);
      // Someone the page can't name reads as "someone you picked", never "A member".
      return named === undefined
        ? t('meetings.errorNotInPoolAny')
        : t('meetings.errorNotInPool', { name: personName(named.displayName, t) });
    }
    if (err.code === ErrorCodes.SERIES_INVALID) return t('planned.errorInvalid');
    return t('meetings.errorGeneric');
  }

  /** A start change moves the end with it, so the duration stays what it was. */
  function moveStart(next: { date?: string; time?: string; zone?: string }) {
    const nextDate = next.date ?? date;
    const nextTime = next.time ?? time;
    const nextZone = next.zone ?? zone;
    const keep = minutes !== null && minutes > 0 ? minutes : 60;
    const end = addMinutes(nextDate, nextTime, nextZone, keep);
    if (next.date !== undefined) setDate(next.date);
    if (next.time !== undefined) setTime(next.time);
    if (next.zone !== undefined) setZone(next.zone);
    if (end !== null) {
      setEndDate(end.date);
      setEndTime(end.time);
    }
  }

  async function handleSubmit() {
    if (locked || submitting || (vaId === null && !standalone)) return;
    if (title.trim().length === 0 || date.length === 0 || time.length === 0) return;
    if (!isValidTimezone(zone) || ruleError !== null) {
      setError(t('meetings.errorValidation'));
      guard.focusErrors();
      return;
    }
    // Just us is the viewer and at least one other person (§D 20).
    if (standalone && inviteIds.size === 0) {
      setFieldErrors({ inviteUserIds: t('meetings.editor.pickSomeone') });
      setError(t('meetings.errorValidation'));
      guard.focusErrors();
      return;
    }
    if (minutes === null || !Number.isInteger(minutes) || minutes < 1 || minutes > 1440) {
      setFieldErrors({ durationMinutes: t('meetings.editor.endInvalid') });
      setError(t('meetings.errorValidation'));
      guard.focusErrors();
      return;
    }
    setSubmitting(true);
    setError(null);
    setNeedsPlan(false);
    setFieldErrors({});
    try {
      const startsAt = zonedCivilToUtc(date, time, zone);
      const inviteUserIds = [...inviteIds];
      const recurring = civilRule.frequency !== 'ONCE';
      if (meeting === undefined && standalone) {
        const body: Omit<MyMeetingCreateRequest, 'virtualAccountId'> = {
          title: title.trim(),
          startsAt,
          durationMinutes: minutes,
          seriesTimezone: zone,
          inviteeUserIds: inviteUserIds,
        };
        if (url.trim()) body.meetingUrl = url.trim();
        if (location.trim()) body.locationText = location.trim();
        if (description.trim()) body.description = description.trim();
        if (reminder !== '') body.reminderLeadMinutes = reminder;
        if (recurring) body.recurrence = recurrenceToWire({ ...civilRule, startOn: date });
        await meetingsApi.createStandalone(body);
        onSaved(t('meetings.created'));
      } else if (meeting === undefined && vaId !== null) {
        const body: MeetingCreateRequest = {
          title: title.trim(),
          startsAt,
          durationMinutes: minutes,
          seriesTimezone: zone,
        };
        if (inviteUserIds.length > 0) body.inviteUserIds = inviteUserIds;
        if (url.trim()) body.meetingUrl = url.trim();
        if (location.trim()) body.locationText = location.trim();
        if (description.trim()) body.description = description.trim();
        if (reminder !== '') body.reminderLeadMinutes = reminder;
        if (recurring) body.recurrence = recurrenceToWire({ ...civilRule, startOn: date });
        await meetingsApi.create(vaId, body);
        onSaved(t('meetings.created'));
      } else if (meeting === undefined) {
        return;
      } else if (past) {
        // The record is fixed: the notes are all that can still change.
        const body: MeetingPatchRequest = {};
        const nextDesc = description.trim() || undefined;
        if ((nextDesc ?? '') !== (meeting.description ?? '')) body.description = nextDesc ?? '';
        await meetingsApi.patch(vaId, meeting.id, body);
        onSaved(t('meetings.saved'));
      } else {
        const body: MeetingPatchRequest = {};
        if (title.trim() !== meeting.title) body.title = title.trim();
        if (!sameUtcInstant(meeting.startsAt, startsAt)) body.startsAt = startsAt;
        if (minutes !== meeting.durationMinutes) body.durationMinutes = minutes;
        if (zone !== meeting.seriesTimezone) body.seriesTimezone = zone;
        const hadRule = meeting.recurrence !== null && meeting.recurrence.frequency !== 'ONCE';
        if (recurring) body.recurrence = recurrenceToWire({ ...civilRule, startOn: date });
        else if (hadRule) body.oneTime = true;
        const nextInvites = [...inviteIds].sort((a, b) => a - b);
        if (nextInvites.join(',') !== servedInvites.join(',')) body.inviteUserIds = inviteUserIds;
        const nextUrl = url.trim() || undefined;
        if ((nextUrl ?? '') !== (meeting.meetingUrl ?? '')) body.meetingUrl = nextUrl ?? '';
        const nextLoc = location.trim() || undefined;
        if ((nextLoc ?? '') !== (meeting.locationText ?? '')) body.locationText = nextLoc ?? '';
        const nextDesc = description.trim() || undefined;
        if ((nextDesc ?? '') !== (meeting.description ?? '')) body.description = nextDesc ?? '';
        if (reminder === '' && meeting.reminderLeadMinutes !== null) body.clearReminder = true;
        else if (reminder !== '' && reminder !== meeting.reminderLeadMinutes) {
          body.reminderLeadMinutes = reminder;
        }
        await meetingsApi.patch(vaId, meeting.id, body);
        onSaved(t('meetings.saved'));
      }
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.MEETING_SCHEDULE_NOT_ALLOWED) {
        // Neither a subscriber nor a Manager the invitees share a virtual account with (§D 21).
        setNeedsPlan(true);
        guard.focusErrors();
      } else if (isApiError(err) && err.code === ErrorCodes.INVITEE_NOT_IN_POOL) {
        // Someone stopped sharing: say who, read the pool again and drop them from the choice.
        const id = backendNumber(err, 'userId');
        setError(errorMessage(err));
        if (id !== null) {
          setInviteIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
        setPoolRead((n) => n + 1);
        guard.focusErrors();
      } else if (isApiError(err) && err.code === ErrorCodes.VALIDATION_FAILED) {
        const next: Partial<Record<MeetingField, string>> = {};
        const result = applyBackendFields(
          err,
          MEETING_FIELDS,
          (field, message) => {
            next[field] = message;
          },
          t,
          guard.focusErrors,
        );
        setFieldErrors(next);
        setError(validationAlert(result, err, t, t('meetings.errorValidation')));
      } else {
        setError(errorMessage(err));
        guard.focusErrors();
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancelMeeting() {
    if (meeting === undefined || (vaId === null && !standalone) || cancelBusy) return;
    setCancelBusy(true);
    setCancelError(null);
    try {
      await meetingsApi.cancelSeries(vaId, meeting.id);
      onSaved(t('meetings.cancelled'));
    } catch {
      setCancelError(t('meetings.errorGeneric'));
    } finally {
      setCancelBusy(false);
    }
  }

  if (cancelling && meeting !== undefined) {
    return (
      <GuardedDialog
        guard={guard}
        confirm={{
          titleId: CANCEL_TITLE_ID,
          onClose: cancelBusy ? undefined : () => setCancelling(false),
        }}
        fullWidth
        maxWidth="sm"
      >
        <ConfirmPanel
          titleId={CANCEL_TITLE_ID}
          title={t('meetings.cancelSeriesTitle', { title: meeting.title })}
          consequence={t('meetings.cancelSeriesConsequence')}
          confirmLabel={t('meetings.editor.cancelMeeting')}
          cancelLabel={t('meetings.keepSeries')}
          destructive
          busy={cancelBusy}
          error={cancelError}
          onConfirm={() => void handleCancelMeeting()}
          onCancel={() => setCancelling(false)}
        />
      </GuardedDialog>
    );
  }

  const canCancel = meeting !== undefined && !locked && !past && meeting.status === 'SCHEDULED';
  const cta =
    meeting === undefined
      ? t('meetings.editor.ctaCreate')
      : past
        ? t('meetings.editor.ctaNotes')
        : t('meetings.editor.ctaSave');
  const submitDisabled =
    submitting || title.trim().length === 0 || date.length === 0 || (vaId === null && !standalone);
  const owner = personName(virtualAccount?.ownerDisplayName, t);
  const organizerName = personName(meeting?.organizer.displayName, t);

  const sharers = locked
    ? (meeting?.invitees ?? [])
        .filter((invite) => invite.status !== 'CANCELLED')
        .map((invite, index) => ({
          key: `i-${invite.userId ?? `m${index}`}`,
          userId: invite.userId,
          name: personName(invite.displayName, t),
          pending: false,
        }))
    : standalone
      ? [
          ...(pool ?? []).map((person) => ({
            key: `p-${person.userId}`,
            userId: person.userId as number | null,
            name: personName(person.displayName, t),
            pending: false,
          })),
        ]
      : [
          ...active.map((share) => ({
            key: `s-${share.id}`,
            userId: share.userId,
            name: personName(share.displayName, t),
            pending: false,
          })),
          ...pending.map((share) => ({
            key: `s-${share.id}`,
            userId: null,
            // An invitation not yet accepted has no name of its own: the owner knows it by the address.
            name: personName(share.displayName ?? share.email, t),
            pending: true,
          })),
        ];
  const peopleLoaded = standalone ? pool !== null : shares !== null;

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      subtitle={
        <DialogSubtitle id={SUBTITLE_ID}>
          {standalone
            ? t('meetings.editor.justUsSubtitle')
            : t('meetings.editorSubtitle', { virtualAccount: virtualAccountName })}
        </DialogSubtitle>
      }
      subtitleId={SUBTITLE_ID}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 700, maxWidth: 'calc(100% - 32px)' } } }}
    >
      <DialogTitle id={TITLE_ID}>
        {meeting ? t('meetings.editTitle') : t('meetings.newTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ mt: 1, gap: 2 }}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          {needsPlan && (
            <Alert
              severity="info"
              data-editor-note="needs-plan"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    // This dialog closes, then the purchase opens (DLG-005).
                    onClose();
                    openSubscribe();
                  }}
                >
                  {t('subscribe.cta')}
                </Button>
              }
            >
              {t('meetings.scheduleNotAllowed')}
            </Alert>
          )}
          {locked && (
            <Box
              data-editor-note="read-only"
              sx={{
                backgroundColor: tokens.hover,
                borderRadius: `${tokens.radius.control}px`,
                p: '10px 14px',
              }}
            >
              <Typography sx={{ fontSize: 13, color: tokens.text }}>
                {standalone
                  ? t('meetings.editor.readOnlyJustUs', { organizer: organizerName })
                  : t('meetings.editor.readOnlyNote', {
                      owner,
                      virtualAccount: virtualAccountName,
                    })}
              </Typography>
            </Box>
          )}
          {past && (
            <Box
              data-editor-note="past"
              sx={{
                backgroundColor: tokens.hover,
                borderRadius: `${tokens.radius.control}px`,
                p: '10px 14px',
              }}
            >
              <Typography sx={{ fontSize: 13, color: tokens.text }}>
                {t('meetings.editor.pastNote')}
              </Typography>
            </Box>
          )}

          {meeting === undefined && allowStandalone && virtualAccountId === undefined && (
            <Box>
              <SegmentedControl<About>
                value={about}
                aria-label={t('meetings.editor.about')}
                filter
                options={[
                  { value: 'virtualAccount', label: t('meetings.editor.aboutVirtualAccount') },
                  { value: 'justUs', label: t('meetings.justUs') },
                ]}
                onChange={(next) => {
                  setAbout(next);
                  setInviteIds(new Set());
                  setFieldErrors({});
                }}
              />
              {standalone && (
                <Typography sx={{ fontSize: 12, color: tokens.textLabel('surfaceRaised'), mt: 1 }}>
                  {t('meetings.editor.justUsNote')}
                </Typography>
              )}
            </Box>
          )}
          {meeting === undefined &&
            !standalone &&
            virtualAccountId === undefined &&
            choices.length === 0 && (
              <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
                {t('meetings.editor.noVirtualAccount')}
              </Typography>
            )}
          {meeting === undefined &&
            !standalone &&
            choices.length > 0 &&
            virtualAccountId === undefined && (
              <Box>
                <FormControl sx={{ width: '100%', maxWidth: 300, ...dense }}>
                  <InputLabel id="meeting-virtual-account">
                    {t('meetings.editor.virtualAccount')}
                  </InputLabel>
                  <Select
                    labelId="meeting-virtual-account"
                    value={pickedVirtualAccountId === null ? '' : String(pickedVirtualAccountId)}
                    onChange={(event) => {
                      setPickedVirtualAccountId(Number(event.target.value));
                      setInviteIds(new Set());
                    }}
                  >
                    {choices.map((choice) => (
                      <MenuItem key={choice.id} value={String(choice.id)}>
                        {choice.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {shares === null
                      ? ' '
                      : sharedWith.length > 0
                        ? t('meetings.editor.sharedWith', { names: sharedWith.join(', ') })
                        : t('meetings.editor.private')}
                  </FormHelperText>
                </FormControl>
              </Box>
            )}

          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5 }}>
            <TextField
              label={t('meetings.titleLabel')}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              disabled={scheduleLocked}
              error={fieldErrors.title !== undefined}
              helperText={fieldErrors.title}
              sx={{ flex: 1, ...dense }}
            />
            <TextField
              label={t('meetings.urlLabel')}
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              disabled={scheduleLocked}
              error={fieldErrors.meetingUrl !== undefined}
              helperText={fieldErrors.meetingUrl}
              sx={{ width: { md: 220 }, ...dense }}
            />
            <TextField
              label={t('meetings.locationLabel')}
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              disabled={scheduleLocked}
              error={fieldErrors.locationText !== undefined}
              helperText={fieldErrors.locationText}
              sx={{ width: { md: 160 }, ...dense }}
            />
          </Stack>
          {meeting?.organizer != null && (
            <Typography sx={{ fontSize: 12, color: tokens.textLabel('surfaceRaised') }}>
              {t('meetings.organizer', { name: personName(meeting.organizer.displayName, t) })}
            </Typography>
          )}

          <Box
            role="group"
            aria-label={t('meetings.editor.schedule')}
            sx={{
              border: `1px solid ${tokens.line}`,
              borderRadius: `${tokens.radius.control}px`,
              p: '14px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5 }}>
              <TextField
                type="date"
                label={t('meetings.editor.startDate')}
                value={date}
                onChange={(event) => {
                  moveStart({ date: event.target.value });
                  setRule((prev) => ({ ...prev, startOn: event.target.value || prev.startOn }));
                }}
                required
                disabled={scheduleLocked}
                error={fieldErrors.startsAt !== undefined}
                helperText={fieldErrors.startsAt}
                sx={{ flex: 1, ...dense }}
              />
              <TextField
                type="time"
                label={t('meetings.timeLabel')}
                value={time}
                onChange={(event) => moveStart({ time: event.target.value })}
                required
                disabled={scheduleLocked}
                sx={{ flex: 1, ...dense }}
              />
              {zones.length > 0 ? (
                <FormControl
                  error={fieldErrors.seriesTimezone !== undefined}
                  sx={{ flex: 1.4, ...dense }}
                >
                  <InputLabel id="meeting-zone">{t('meetings.editor.startZone')}</InputLabel>
                  <Select
                    labelId="meeting-zone"
                    value={zone}
                    disabled={scheduleLocked}
                    onChange={(event) => moveStart({ zone: event.target.value })}
                  >
                    {zoneOptions.map((z) => (
                      <MenuItem key={z} value={z}>
                        {z}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.seriesTimezone !== undefined && (
                    <FormHelperText>{fieldErrors.seriesTimezone}</FormHelperText>
                  )}
                </FormControl>
              ) : (
                <TextField
                  label={t('meetings.editor.startZone')}
                  value={zone}
                  onChange={(event) => moveStart({ zone: event.target.value })}
                  disabled={scheduleLocked}
                  error={fieldErrors.seriesTimezone !== undefined}
                  helperText={fieldErrors.seriesTimezone}
                  sx={{ flex: 1.4, ...dense }}
                />
              )}
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5 }}>
              <TextField
                type="date"
                label={t('meetings.editor.endDate')}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                disabled={scheduleLocked}
                error={fieldErrors.durationMinutes !== undefined}
                helperText={fieldErrors.durationMinutes}
                sx={{ flex: 1, ...dense }}
              />
              <TextField
                type="time"
                label={t('meetings.editor.endTime')}
                value={endTime}
                onChange={(event) => setEndTime(event.target.value)}
                disabled={scheduleLocked}
                sx={{ flex: 1, ...dense }}
              />
              <TextField
                label={t('meetings.editor.endZone')}
                value={zone}
                // Read-only, not disabled: its note is text a person reads, and a disabled field's
                // helper is the disabled ink (below 4.5:1 in dark).
                slotProps={{ input: { readOnly: true } }}
                helperText={t('meetings.editor.endZoneNote')}
                sx={{ flex: 1.4, ...dense }}
              />
            </Stack>
            <RecurrenceInline
              rule={civilRule}
              onChange={(next) => setRule({ ...next, startOn: date || next.startOn })}
              startOn={date || rule.startOn}
              disabled={scheduleLocked}
            />
          </Box>

          <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
            <Typography
              variant="overline"
              component="legend"
              sx={{ display: 'block', width: '100%', p: 0 }}
            >
              {t('meetings.editor.invitations')}
            </Typography>
            <Typography sx={{ fontSize: 12, color: tokens.textLabel('surfaceRaised'), mb: 1 }}>
              {standalone
                ? t('meetings.editor.invitationsHelpJustUs')
                : t('meetings.editor.invitationsHelp')}
            </Typography>
            {standalone && !locked && poolFailure !== null && (
              <ErrorState
                variant="section"
                error={poolFailure.error}
                what={t('errors.what.meetingPeople')}
                onRetry={() => setPoolRead((n) => n + 1)}
              />
            )}
            {sharers.length === 0 && (peopleLoaded || locked) && (
              <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
                {standalone ? t('meetings.editor.noPeopleJustUs') : t('meetings.editor.noPeople')}
              </Typography>
            )}
            <Stack sx={{ gap: 1 }}>
              {sharers.map((person) => {
                const invited = person.userId !== null && inviteIds.has(person.userId);
                const state = invited
                  ? t('meetings.editor.invited')
                  : t('meetings.editor.notInvited');
                return (
                  <Stack
                    key={person.key}
                    direction="row"
                    sx={{ alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 500,
                        backgroundColor: tokens.brand,
                        color: tokens.brandInk,
                      }}
                    >
                      {initialsOf(person.name)}
                    </Box>
                    <Typography sx={{ fontSize: 14, minWidth: 0, overflowWrap: 'anywhere' }}>
                      {person.name}
                    </Typography>
                    {person.pending && (
                      <Pill tone="warning">{t('meetings.editor.sharePending')}</Pill>
                    )}
                    {person.pending && (
                      <Typography sx={{ fontSize: 11, color: tokens.textLabel('surfaceRaised') }}>
                        {t('meetings.editor.pendingNote')}
                      </Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Box
                      component="button"
                      type="button"
                      aria-pressed={invited}
                      aria-label={t('meetings.editor.inviteToggle', { name: person.name, state })}
                      disabled={scheduleLocked || person.pending}
                      onClick={() => {
                        const id = person.userId;
                        if (id === null) return;
                        setInviteIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(id)) next.delete(id);
                          else next.add(id);
                          return next;
                        });
                      }}
                      sx={{
                        borderRadius: '11px',
                        padding: '4px 12px',
                        font: 'inherit',
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: scheduleLocked || person.pending ? 'default' : 'pointer',
                        border: `1px solid ${invited ? tokens.brandBoundary : tokens.line}`,
                        backgroundColor: invited ? tokens.selected : 'transparent',
                        color: invited ? tokens.link : tokens.text2,
                        '&:disabled': { color: tokens.text3, borderColor: tokens.line },
                        '&:focus-visible': {
                          outline: `2px solid ${tokens.focusRing}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      {state}
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
            {fieldErrors.inviteUserIds !== undefined && (
              <FormHelperText error>{fieldErrors.inviteUserIds}</FormHelperText>
            )}
          </Box>

          <TextField
            // A Just us meeting's description is its agenda (§D 20, the session's Agenda card).
            label={standalone ? t('meetings.session.agenda') : t('meetings.editor.notes')}
            placeholder={
              standalone ? t('meetings.session.agendaSub') : t('meetings.editor.notesPlaceholder')
            }
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={locked}
            multiline
            minRows={3}
            error={fieldErrors.description !== undefined}
            helperText={fieldErrors.description}
            sx={{
              ...dense,
              '& .MuiOutlinedInput-root': { minHeight: 88, alignItems: 'flex-start' },
            }}
          />
          <FormControl
            error={fieldErrors.reminderLeadMinutes !== undefined}
            sx={{ maxWidth: 300, ...dense }}
          >
            <InputLabel id="meeting-reminder">{t('meetings.reminderLabel')}</InputLabel>
            <Select
              labelId="meeting-reminder"
              value={reminder === '' ? '' : String(reminder)}
              disabled={scheduleLocked}
              displayEmpty
              onChange={(event) =>
                setReminder(event.target.value === '' ? '' : Number(event.target.value))
              }
            >
              {REMINDERS.map((row) => (
                <MenuItem key={row.key} value={row.value === '' ? '' : String(row.value)}>
                  {t(`meetings.${row.key}`)}
                </MenuItem>
              ))}
            </Select>
            {fieldErrors.reminderLeadMinutes !== undefined && (
              <FormHelperText>{fieldErrors.reminderLeadMinutes}</FormHelperText>
            )}
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {canCancel && (
            <Button
              color="error"
              data-confirm-opener={CANCEL_OPENER}
              onClick={() => setCancelling(true)}
              sx={{ color: tokens.errorText }}
            >
              {t('meetings.editor.cancelMeeting')}
            </Button>
          )}
        </Box>
        <Stack direction="row" sx={{ gap: 1 }}>
          <Button onClick={guard.requestClose}>{t('common.close')}</Button>
          {!locked && (
            <SubmitButton
              onClick={() => void handleSubmit()}
              disabled={submitDisabled}
              loading={submitting}
            >
              {cta}
            </SubmitButton>
          )}
        </Stack>
      </DialogActions>
    </GuardedDialog>
  );
}
